// https://medium.com/@mircea.calugaru/react-quill-editor-with-full-toolbar-options-and-custom-buttons-undo-redo-176d79f8d375

import ReactQuill from 'react-quill';
import { SupportImageSvg, SupportVideoSvg, UpSvg } from '../../../icons';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface Props {
    idValue?: string
    classNameValue: string
    onSubmit: Function
    placeholder?: string
    readOnly?: boolean
}

const FORMATS: string[] = [ 'bold', 'italic', 'underline', 'link', 'code-block' ];

const MODULES = {
    toolbar: {
        container: "#toolbar",
    },
    history: {
        delay: 500,
        maxStack: 100,
        userOnly: true,
    },
};

const MAX_IMAGE_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_VIDEO_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

const SUPPORTED_IMAGE_MIME_TYPES = [ 'image/jpeg', 'image/png', 'image/webp' ];
const SUPPORTED_VIDEO_MIME_TYPES = [ 'video/mp4', 'video/mov', 'video/avi', 'video/webm', 'video/x-matroska' ];


export default function SupportChat(props: Props) {
    const { idValue, classNameValue, onSubmit, placeholder, readOnly } = props;

    const refEditorInput = useRef<any>(null);
    const { t } = useTranslation();

    const [ text, setText ] = useState<string>('');
    const textRef = useRef<any>(text);

    useEffect(() => {
        textRef.current = text;
    }, [ text ]);

    useEffect(() => {
        if (!idValue && refEditorInput?.current?.editor) {
            refEditorInput.current.editor.focus();
        }
    }, []);

    useEffect(() => {
        if (!refEditorInput?.current || !refEditorInput?.current?.editor) {
            return;
        }

        const editor = refEditorInput.current.getEditor();
        const root = editor.root;

        if (placeholder) {
            root.setAttribute('data-placeholder', placeholder);
        } else {
            root.removeAttribute('data-placeholder');
        }
    }, [ placeholder ]);

    useLayoutEffect(() => {
        if (!idValue) {
            return;
        }

        const resize_ob = new ResizeObserver((entries) => {
            const rect = entries[0].contentRect;
            const newHeight = rect.height;

            (document.getElementById(idValue) as HTMLElement).style.height = `${newHeight}px`;
        });

        resize_ob.observe(document.getElementById('chat-ql-id') as HTMLElement);

        return () => {
            resize_ob.unobserve(document.getElementById('chat-ql-id') as HTMLElement);
        };
    }, []);

    const _imageHandler = useCallback(() => {
        if (readOnly) {
            return;
        }

        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file: File | undefined = input?.files?.[0];

            if (!file) {
                toast.error('ERROR_LOADING_IMAGE');
                console.error('ERROR_LOADING_IMAGE:', { input });
                return
            }

            const isValid = _validateFile(file, SUPPORTED_IMAGE_MIME_TYPES, MAX_IMAGE_FILE_SIZE);

            if (!isValid) {
                return;
            }

            const textValue = _isEmptyText() ? null : textRef.current;

            onSubmit(textValue, file);

            if (textValue) {
                setText('');
            }
        };
    }, [ refEditorInput, readOnly ]);

    const _videoHandler = useCallback(() => {
        if (readOnly) {
            return;
        }

        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'video/*');
        input.click();

        input.onchange = async () => {
            const file: File | undefined = input?.files?.[0];

            if (!file) {
                toast.error('ERROR_LOADING_VIDEO');
                console.error('ERROR_LOADING_VIDEO:', { input });
                return
            }

            const isValid = _validateFile(file, SUPPORTED_VIDEO_MIME_TYPES, MAX_VIDEO_FILE_SIZE);

            if (!isValid) {
                return;
            }

            const textValue = _isEmptyText() ? null : textRef.current;

            onSubmit(textValue, null, file);

            if (textValue) {
                setText('');
            }
        };
    }, [ refEditorInput, readOnly ]);

    const _validateFile = (file: File, supportedTypes: string[], maxSize: number): boolean => {
        const errorMessageFormat: string = t('support.errorMessageFormat');
        const errorMessageSize: string = t('support.errorMessageSize');

        if (!supportedTypes.includes(file.type)) {
            toast.warning(errorMessageFormat);
            console.error(errorMessageFormat, { file });
            return false;
        }

        if (file.size > maxSize) {
            toast.warning(`${errorMessageSize} ${(maxSize / (1024 * 1024)).toFixed(2)} MB.`);
            console.error(`${errorMessageSize} ${(maxSize / (1024 * 1024)).toFixed(2)} MB.`, { file });
            return false;
        }

        return true;
    };

    const _isEmptyText = (): boolean => {
        return Boolean(!textRef.current || textRef.current === '<p><br></p>');
    }

    const _onSubmit = () => {
        if (readOnly) {
            return;
        }

        if (_isEmptyText()) {
            toast.warning(t('support.descriptionEmptyWarning'));
            return;
        }

        onSubmit(textRef.current);
        setText('');

        if (!idValue) {
            return;
        }

        setTimeout(() => {
            const elem = document.getElementById(idValue) as HTMLElement;
            elem.scrollTo(0, elem.scrollHeight);
        }, 200);
    }

    return (
        <div id="chat-ql-id" className={classNameValue}>
            <ReactQuill
                ref={refEditorInput}
                theme="snow"
                value={text}
                onChange={setText}
                modules={MODULES}
                formats={FORMATS}
                readOnly={readOnly}
            />
            <div id="toolbar">
                <div className="ql-formats-left">
                    <button className="ql-bold"/>
                    <button className="ql-italic"/>
                    <button className="ql-underline"/>
                    <button className="ql-link"/>
                    <button className="ql-code-block"/>
                    <div className="ql-custom-icon" onClick={_imageHandler}>
                        <SupportImageSvg/>
                    </div>
                    <div className="ql-custom-icon" onClick={_videoHandler}>
                        <SupportVideoSvg/>
                    </div>
                </div>
                <div className="ql-formats-right">
                    <button className="ql-formats-right-btn" onClick={_onSubmit}>
                        <UpSvg/>
                    </button>
                </div>
            </div>
        </div>
    );
}